import React, { useEffect, useState } from 'react';
import { ScreenPublicModal } from '../../components/ScreenPublicModal';

interface ScreenItem {
 client_hash: string;
 name: string;
 address: string | null;
 latitude: number | null;
 longitude: number | null;
 thumbnail_url: string | null;
 photo_count: number;
 mo_family_hash: string | null;
 family_name: string | null;
}

interface ScreensResponse {
 items: ScreenItem[];
 total: number;
 page: number;
 limit: number;
 include_descendants: boolean;
}

/**
 * 모패밀리 상세의 스크린 탭. 직접 보유 + (옵션) 하위 패밀리 활성 mo_clients
 * 카드 그리드. 비owner/비member 도 접근 가능 (공개-안전 필드만). 카드 클릭
 * → ScreenPublicModal.
 */
export const ScreensTab: React.FC<{ moFamilyHash: string }> = ({ moFamilyHash }) => {
 const [data, setData] = useState<ScreensResponse | null>(null);
 const [error, setError] = useState<string | null>(null);
 const [page, setPage] = useState(0);
 const [includeDescendants, setIncludeDescendants] = useState(false);
 const [selected, setSelected] = useState<string | null>(null);
 const limit = 12;

 useEffect(() => {
  setError(null);
  const params = new URLSearchParams({
   page: String(page),
   limit: String(limit),
   include_descendants: String(includeDescendants),
  });
  fetch(`/mofamily/${moFamilyHash}/public-screens?${params.toString()}`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!j.success) { setError(j.error || '조회 실패'); return; }
    setData(j.data);
   })
   .catch(err => setError(err.message || '조회 실패'));
 }, [moFamilyHash, page, includeDescendants]);

 // 모패밀리 변경되면 페이지/모드 초기화.
 useEffect(() => {
  setPage(0);
  setIncludeDescendants(false);
 }, [moFamilyHash]);

 const switchToDescendants = () => {
  setIncludeDescendants(true);
  setPage(0);
 };
 const switchToDirect = () => {
  setIncludeDescendants(false);
  setPage(0);
 };

 if (error) return <div className="bg-red-50 text-red-700 p-3 text-sm">{error}</div>;
 if (!data) return <div className="text-gray-500 py-8 text-center text-sm">로딩 중...</div>;

 if (data.total === 0) {
  return (
   <div className="bg-white py-12 text-center">
    <p className="text-gray-400 text-sm mb-4">
     {includeDescendants
      ? '이 모패밀리와 하위 패밀리에 등록된 스크린이 없습니다.'
      : '이 모패밀리에 직접 등록된 스크린이 없습니다.'}
    </p>
    {!includeDescendants && (
     <button
      type="button"
      onClick={switchToDescendants}
      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 text-sm"
     >
      하위 패밀리에 등록된 스크린 모두 보기
     </button>
    )}
   </div>
  );
 }

 const totalPages = Math.max(1, Math.ceil(data.total / data.limit));

 return (
  <>
   <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
    <div className="text-sm text-gray-500">
     총 {data.total}대
     {data.include_descendants && <span className="ml-1 text-gray-400">(하위 패밀리 포함)</span>}
    </div>
    <button
     type="button"
     onClick={data.include_descendants ? switchToDirect : switchToDescendants}
     className="text-xs text-blue-600 hover:underline"
    >
     {data.include_descendants ? '직접 보유 스크린만 보기' : '하위 패밀리 포함 보기'}
    </button>
   </div>
   <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
    {data.items.map(s => (
     <button
      key={s.client_hash}
      type="button"
      onClick={() => setSelected(s.client_hash)}
      className="bg-gray-50 border border-gray-200 overflow-hidden flex flex-col text-left hover:border-blue-400 transition-colors"
     >
      {s.thumbnail_url ? (
       <div className="relative aspect-video bg-gray-200">
        <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
        {s.photo_count > 1 && (
         <span className="absolute bottom-1 right-1 bg-black bg-opacity-60 text-white text-[10px] px-1.5 py-0.5">
          +{s.photo_count - 1}
         </span>
        )}
       </div>
      ) : (
       <div className="aspect-video bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
        사진 없음
       </div>
      )}
      <div className="p-2 text-xs">
       {s.address ? (
        <div className="text-gray-700 line-clamp-2">{s.address}</div>
       ) : (
        <div className="text-gray-400">위치 미등록</div>
       )}
       {s.name && <div className="text-gray-400 mt-1 truncate">{s.name}</div>}
       {data.include_descendants && s.family_name && s.mo_family_hash !== moFamilyHash && (
        <div className="text-blue-600 mt-1 truncate">패밀리: {s.family_name}</div>
       )}
      </div>
     </button>
    ))}
   </div>

   {totalPages > 1 && (
    <div className="flex items-center justify-center gap-2 mt-4">
     <button
      onClick={() => setPage(Math.max(0, page - 1))}
      disabled={page === 0}
      className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50"
     >
      이전
     </button>
     <span className="text-sm text-gray-600">{page + 1} / {totalPages}</span>
     <button
      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
      disabled={page >= totalPages - 1}
      className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50"
     >
      다음
     </button>
    </div>
   )}

   <ScreenPublicModal clientHash={selected} onClose={() => setSelected(null)} />
  </>
 );
};
